import type { ILocalTrack, IMicrophoneAudioTrack } from 'agora-rtc-sdk-ng';
import AgoraRTC, { IAgoraRTCClient } from 'agora-rtc-sdk-ng';

import { StudioLogger } from '@studio/logger/StudioLogger';
import { setupAudioDeviceListeners } from '../events/setupDeviceListeners';

const LOG = new StudioLogger('agoraAudioLogger');

/* eslint-disable no-unused-vars */
interface Actions {
  showNotification: (...args: any[]) => any;
  handleVideoInputChanged: (...args: any[]) => any;
  handleAudioInputChanged: (...args: any[]) => any;
  addToAttendeeTiles: (...args: any[]) => any;
}

/**
 * Agora SDK Audio Helper Functions
 * These functions provide a consistent interface for Agora audio/video operations
 */

/**
 * Sets the mute status of the local user's audio stream.
 * If there is no published mic track (mic failed at join, or lost on reconnect),
 * muting is a no-op since nothing is being sent. Unmuting creates a new track on
 * the first available microphone, publishes it, then applies the requested state.
 * @param localAudioTrack - The published Agora mic track, or undefined if there is none
 * @param isMuted - Whether to mute (true) or unmute (false) the audio
 * @param client
 * @param dispatch
 * @param deviceActions
 * @returns Promise<void>
 */
export async function muteLocalAudioStream(
  localAudioTrack: IMicrophoneAudioTrack | undefined,
  isMuted: boolean,
  client: IAgoraRTCClient,
  dispatch: (action: any) => void = () => {},
  deviceActions: Actions = {} as Actions
): Promise<void> {
  if (localAudioTrack) {
    await setLocalAudioMuted(localAudioTrack, isMuted);
    return;
  }

  // No published track means nothing is being sent, so a mute is already satisfied.
  if (isMuted) return;

  let newAudioTrack: IMicrophoneAudioTrack | undefined;
  try {
    const [microphone] = await AgoraRTC.getMicrophones();
    if (!microphone) throw new Error('No microphone found');

    newAudioTrack = await AgoraRTC.createMicrophoneAudioTrack({
      microphoneId: microphone.deviceId,
      ANS: true,
      AEC: true
    });
    await client.publish(newAudioTrack);
    await setLocalAudioMuted(newAudioTrack, isMuted);

    setupAudioDeviceListeners(newAudioTrack, dispatch, deviceActions);
    LOG.warn(
      `No published mic track was found when unmuting (this is unexpected), recovered by creating and publishing a new track for device: ${microphone.deviceId}`
    );
  } catch (error) {
    LOG.error('Failed to create new audio track when toggling audio:', error);
    // Never leave a mic we just published live behind a failed toggle. A failed
    // unpublish must not skip close() or replace the original error.
    try {
      if (newAudioTrack && client.localTracks.includes(newAudioTrack)) {
        LOG.warn('Unpublishing the new audio track after a failed toggle; it was published but could not be used');
        await client.unpublish(newAudioTrack);
      }
    } catch (unpublishError) {
      LOG.error('Failed to unpublish new audio track during cleanup:', unpublishError);
    } finally {
      newAudioTrack?.close();
    }
    throw error;
  }
}

/**
 * Applies the mute status to a local audio track
 * @param localAudioTrack - The Agora local audio track instance
 * @param isMuted - Whether to mute (true) or unmute (false) the audio
 * @returns Promise<void>
 */
async function setLocalAudioMuted(localAudioTrack: IMicrophoneAudioTrack, isMuted: boolean): Promise<void> {
  try {
    await localAudioTrack.setMuted(isMuted);
    LOG.info(`Agora Local audio ${isMuted ? 'muted' : 'unmuted'}`);
  } catch (error) {
    LOG.error(`Failed to ${isMuted ? 'mute' : 'unmute'} local audio stream:`, error);
    throw error;
  }
}

/**
 * Switches the audio input device for the local audio track
 * @param client - The Agora RTC client instance
 * @param deviceId - The new audio input device ID
 * @returns Promise<IMicrophoneAudioTrack> - The audio track with the switched device
 */
export async function switchAudioInput(
  client: IAgoraRTCClient,
  deviceId: string,
  dispatch: (action: any) => void = () => {},
  deviceActions: Actions = {} as Actions
): Promise<IMicrophoneAudioTrack | null> {
  try {
    if (!client) {
      throw new Error('Agora RTC client is not initialized.');
    }

    const currentAudioTrack = client.localTracks.find(
      (track: ILocalTrack) => track.trackMediaType === 'audio'
    ) as IMicrophoneAudioTrack | null;

    if (!currentAudioTrack) {
      try {
        const newAudioTrack: IMicrophoneAudioTrack = await AgoraRTC.createMicrophoneAudioTrack({
          microphoneId: deviceId,
          ANS: true,
          AEC: true
        });

        setupAudioDeviceListeners(newAudioTrack, dispatch, deviceActions);
        await client.publish(newAudioTrack);
        LOG.info(`Successfully created new audio track for device: ${deviceId}`);
        return newAudioTrack;
      } catch (error) {
        LOG.error('Failed to create new audio track when switching audio input:', error);
        return null;
      }
    }

    await currentAudioTrack.setDevice(deviceId);
    LOG.info(`Successfully switched audio input device to: ${deviceId}`);
    return currentAudioTrack;
  } catch (error) {
    LOG.error(`Failed to switch audio input device to ${deviceId}:`, error);
    throw error;
  }
}

/**
 * Lists all available audio input devices
 * @returns Promise<MediaDeviceInfo[]> - Array of available audio input devices
 */
export async function listAudioInputDevices(): Promise<MediaDeviceInfo[]> {
  try {
    const devices = await AgoraRTC.getDevices();
    const audioInputDevices = devices.filter(device => device.kind === 'audioinput');

    return audioInputDevices;
  } catch (error) {
    LOG.error('Failed to list audio input devices:', error);
    throw error;
  }
}
